import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Service areas with actual coordinates and hrefs
const serviceAreas = [
  { name: "Houston", lat: 29.7604, lng: -95.3698, isCenter: true, href: "/houston-roofing" },
  { name: "Conroe", lat: 30.3119, lng: -95.4560, href: "/roofing-conroe-tx" },
  { name: "The Woodlands", lat: 30.1658, lng: -95.4613, href: "/roofing-the-woodlands-tx" },
  { name: "Spring", lat: 30.0799, lng: -95.4172, href: "/roofing-spring-tx" },
  { name: "Cypress", lat: 29.9691, lng: -95.6972, href: null }, // No page yet
  { name: "Aldine", lat: 29.9288, lng: -95.3799, href: "/roofing-aldine-tx" },
  { name: "Kingwood", lat: 30.0385, lng: -95.2394, href: "/roofing-kingwood-tx" },
  { name: "Humble", lat: 29.9988, lng: -95.2622, href: "/roofing-humble-tx" },
  { name: "Atascocita", lat: 29.9910, lng: -95.1769, href: "/roofing-atascocita-tx" },
  { name: "Porter / New Caney", lat: 30.1510, lng: -95.1499, href: null }, // No page yet
  { name: "Pearland", lat: 29.5636, lng: -95.2860, href: null }, // No page yet
];

// Custom marker icon using Altum brand colors
const createCustomIcon = (isCenter: boolean, name: string, hasLink: boolean) => {
  const size = isCenter ? 28 : 18;
  const color = isCenter ? "#E60012" : "#00274D";
  const fontSize = isCenter ? "12px" : "10px";
  const fontWeight = isCenter ? "700" : "600";
  const cursor = hasLink ? "pointer" : "default";
  
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div class="marker-wrapper" style="
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: ${cursor};
        transition: transform 0.2s ease;
      ">
        <div class="marker-dot" style="
          width: ${size}px;
          height: ${size}px;
          background-color: ${color};
          border: 4px solid white;
          border-radius: 50%;
          box-shadow: 0 3px 10px rgba(0,0,0,0.35);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          ${isCenter ? 'animation: pulse 2s infinite;' : ''}
        "></div>
        <span style="
          margin-top: 4px;
          font-family: 'Montserrat', sans-serif;
          font-size: ${fontSize};
          font-weight: ${fontWeight};
          color: ${color};
          text-shadow: 0 0 3px white, 0 0 5px white, 0 0 8px white;
          white-space: nowrap;
        ">${name}</span>
      </div>
    `,
    iconSize: [80, 50],
    iconAnchor: [40, size / 2],
  });
};

const ServiceAreaMap = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map centered on Houston - allow basic interaction
    const map = L.map(mapContainerRef.current, {
      center: [29.85, -95.35],
      zoom: 9,
      scrollWheelZoom: false,
      zoomControl: true,
      dragging: true,
      touchZoom: true,
      doubleClickZoom: true,
      boxZoom: false,
      keyboard: false,
      attributionControl: true,
    });

    mapRef.current = map;

    // Add CartoDB Positron tiles for a clean, minimal style
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    // Add markers for each service area
    serviceAreas.forEach((area) => {
      const hasLink = !!area.href;
      const marker = L.marker([area.lat, area.lng], {
        icon: createCustomIcon(area.isCenter || false, area.name, hasLink),
      }).addTo(map);

      // Add popup with area name and link
      const popupContent = area.href
        ? `<div style="
            font-family: 'Montserrat', sans-serif;
            text-align: center;
            padding: 4px 8px;
          ">
            <div style="
              font-weight: ${area.isCenter ? '700' : '600'};
              font-size: ${area.isCenter ? '14px' : '12px'};
              color: ${area.isCenter ? '#E60012' : '#00274D'};
              margin-bottom: 4px;
            ">
              ${area.name}
              ${area.isCenter ? '<br><span style="font-size: 10px; color: #666;">Headquarters</span>' : ''}
            </div>
            <a href="${area.href}" style="
              display: inline-block;
              margin-top: 4px;
              font-size: 11px;
              color: #E60012;
              font-weight: 600;
              text-decoration: none;
            ">View Services →</a>
          </div>`
        : `<div style="
            font-family: 'Montserrat', sans-serif;
            font-weight: ${area.isCenter ? '700' : '600'};
            font-size: ${area.isCenter ? '14px' : '12px'};
            color: ${area.isCenter ? '#E60012' : '#00274D'};
            padding: 4px 8px;
          ">
            ${area.name}
            ${area.isCenter ? '<br><span style="font-size: 10px; color: #666;">Headquarters</span>' : ''}
          </div>`;

      marker.bindPopup(popupContent, {
        closeButton: false,
        className: "custom-popup",
      });

      // Show popup on hover
      marker.on("mouseover", function () {
        this.openPopup();
      });
      marker.on("mouseout", function () {
        this.closePopup();
      });

      // Navigate on click if link exists
      if (area.href) {
        marker.on("click", function () {
          window.location.href = area.href as string;
        });
      }
    });

    // Add custom CSS for animations and hover effects
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(230, 0, 18, 0.4); }
        70% { box-shadow: 0 0 0 15px rgba(230, 0, 18, 0); }
        100% { box-shadow: 0 0 0 0 rgba(230, 0, 18, 0); }
      }
      .custom-marker:hover .marker-wrapper {
        transform: scale(1.15);
      }
      .custom-marker:hover .marker-dot {
        box-shadow: 0 4px 14px rgba(0,0,0,0.45);
      }
      .custom-popup .leaflet-popup-content-wrapper {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      }
      .custom-popup .leaflet-popup-tip {
        background: white;
      }
      .leaflet-container {
        font-family: 'Montserrat', sans-serif;
      }
    `;
    document.head.appendChild(style);

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      style.remove();
    };
  }, []);

  return (
    <div
      ref={mapContainerRef}
      className="w-full h-[450px] md:h-[500px]"
      style={{ zIndex: 1 }}
    />
  );
};

export default ServiceAreaMap;
